import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { PRODUCTS, CATEGORIES } from "../data/products";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import ProductCard from "./ProductCard";
import { ratingFor } from "../lib/ratings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map a Shopify product to our card shape.
const toCard = (p, category) => ({
  id: p.handle || p.id,
  name: p.title,
  category,
  price: Math.round(p.price || 0),
  originalPrice: p.compareAtPrice ? Math.round(p.compareAtPrice) : null,
  badge: null,
  rating: ratingFor(p.handle || p.id),
  image: p.image || null,
  variantId: p.variantId,
  availableForSale: p.availableForSale !== false,
  quantityAvailable: typeof p.quantityAvailable === "number" ? p.quantityAvailable : null,
  currentlyNotInStock: !!p.currentlyNotInStock,
});

// Categorise a Shopify product by its title so every published item lands in
// the right tab without needing a redeploy.
const categorise = (title) => {
  const t = (title || "").toLowerCase();
  if (/\bboardroom\b/.test(t)) return "boardroom-premium";
  if (/shantanu|nikhil|\bs&n\b|\bs and n\b|\bsn\s/.test(t)) return "shantanu-nikhil";
  return "festive-express";
};

const ProductGrid = () => {
  const [tab, setTab] = useState(CATEGORIES[0].id);
  const [liveByCat, setLiveByCat] = useState({});

  useEffect(() => {
    const handler = (e) => e.detail && setTab(e.detail);
    window.addEventListener("select-tab", handler);
    return () => window.removeEventListener("select-tab", handler);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/shop/products`);
        const data = await res.json();
        if (cancelled || !data.configured) return;
        const buckets = { all: [], "boardroom-premium": [], "shantanu-nikhil": [], "festive-express": [] };
        (data.products || []).forEach((p) => {
          const cat = categorise(p.title);
          const card = toCard(p, cat);
          buckets[cat].push(card);
        });
        // "All" tab: cluster products by collection (Boardroom → S&N → Festive)
        buckets.all = [
          ...buckets["boardroom-premium"].map((c) => ({ ...c, category: "all" })),
          ...buckets["shantanu-nikhil"].map((c) => ({ ...c, category: "all" })),
          ...buckets["festive-express"].map((c) => ({ ...c, category: "all" })),
        ];
        setLiveByCat(buckets);
      } catch (e) {
        /* silent — fall back to hardcoded */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const productsByCat = useMemo(() => {
    const out = {};
    CATEGORIES.forEach((c) => {
      const live = liveByCat[c.id];
      if (live && live.length > 0) out[c.id] = live;
      else out[c.id] = PRODUCTS.filter((p) => p.category === c.id);
    });
    return out;
  }, [liveByCat]);

  return (
    <section id="shop" className="relative z-10 scroll-mt-28 bg-[#0B0F19] py-24 md:py-32" data-testid="product-grid-section">
      <div className="pointer-events-none absolute right-0 top-1/4 h-96 w-96 bloom-gold opacity-40" />

      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-14 text-center"
        >
          <span className="font-sans text-xs uppercase tracking-[0.3em] text-[#D4AF37]">The Boutique</span>
          <h2 className="mx-auto mt-5 max-w-3xl font-serif text-4xl leading-[1.08] tracking-tight text-[#F3EEE3] md:text-6xl">
            Luxury collections, <span className="italic text-[#D4AF37]">delivered in 48 hours</span>
          </h2>
        </motion.div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="mx-auto mb-12 flex h-auto w-full max-w-3xl flex-wrap justify-center gap-2 bg-transparent p-0">
            {CATEGORIES.map((cat) => (
              <TabsTrigger
                key={cat.id}
                value={cat.id}
                data-testid={`tab-${cat.id}`}
                className="rounded-full border border-[#F3E5AB]/15 bg-[#0d1220] px-5 py-2.5 font-sans text-sm tracking-wide text-[#A9B0C2] transition-all duration-300 data-[state=active]:border-[#D4AF37] data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0B0F19] data-[state=active]:shadow-[0_0_22px_rgba(212,175,55,0.4)] hover:text-[#F3EEE3]"
              >
                {cat.name}
              </TabsTrigger>
            ))}
          </TabsList>

          {CATEGORIES.map((cat) => {
            const items = productsByCat[cat.id] || [];
            return (
              <TabsContent key={cat.id} value={cat.id} className="mt-0 focus-visible:outline-none">
                <p className="mb-8 text-center font-sans text-sm text-[#8B93A7]">{cat.tagline}</p>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                  {items.map((product, i) => (
                    <ProductCard key={product.id} product={product} index={i} />
                  ))}
                </div>
              </TabsContent>
            );
          })}
        </Tabs>
      </div>
    </section>
  );
};

export default ProductGrid;
